import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import '../styles/ai.css';

interface AiStatus {
  enabled: boolean;
  simulated: boolean;
  model: string;
  creditsUsed: number;
  creditsLimit: number;
  note: string;
}
interface Field {
  field: string;
  label: string;
  value: unknown;
  confidence: number;
  protected: boolean;
}
interface Finding {
  severity: 'info' | 'warning' | 'critical';
  title: string;
  detail: string;
}
interface Proposal {
  id: string;
  summary: string;
  fields: Field[];
  findings: Finding[];
  questions: string[];
  text?: string;
  protectedFields: string[];
  simulated: boolean;
}

const SUGGESTIONS = [
  'Crea una factura para Aurora por el hito 2 del contrato',
  'Redacta el alcance de un contrato de desarrollo de software a medida',
  'Check whether this deal has any compliance risk',
  'Prepare a consulting quotation for 40 hours',
];

function renderValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export function AiAssistant() {
  const { company } = useSession();
  const [status, setStatus] = useState<AiStatus | null>(null);
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());
  const [applied, setApplied] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.get<AiStatus>('/ai/status').then(setStatus).catch(() => void 0);
  }, [company]);

  const send = async (text?: string) => {
    const p = (text ?? prompt).trim();
    if (p.length < 3) return;
    setBusy(true);
    setErr(null);
    setProposal(null);
    setApplied(false);
    setConfirmed(new Set());
    try {
      const res = await api.post<Proposal>('/ai/command', { prompt: p });
      setProposal(res);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'La IA no pudo responder.');
    } finally {
      setBusy(false);
    }
  };

  const toggle = (field: string) => {
    setConfirmed((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });
  };

  const allConfirmed = proposal ? proposal.protectedFields.every((f) => confirmed.has(f)) : false;

  const apply = async () => {
    if (!proposal) return;
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/ai/${proposal.id}/apply`, {
        accept: true,
        confirmedFields: Array.from(confirmed),
      });
      setApplied(true);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'No se pudo aplicar la propuesta.');
    } finally {
      setBusy(false);
    }
  };

  const protectedFieldObjs = proposal?.fields.filter((f) => f.protected) ?? [];
  const plainFields = proposal?.fields.filter((f) => !f.protected) ?? [];

  return (
    <main className="main">
      <div className="page-head">
        <div className="page-head__row">
          <div className="page-head__title">
            <span className="eyebrow">Asistente</span>
            <h1 className="h1">Ask in your own words</h1>
            <p className="page-head__sub">
              La IA redacta, extrae y propone. Nunca cambia por su cuenta un importe, una fecha, un
              a bank or a commission: it presents that data for you to confirm one by one.
            </p>
          </div>
          {status && (
            <span className="ai-status" title={status.note}>
              <span className={`ai-status__dot${status.simulated ? ' ai-status__dot--sim' : ''}`} />
              {status.simulated ? 'Modo simulado' : status.model}
              {status.creditsLimit > 0 && (
                <span className="num">· {status.creditsUsed}/{status.creditsLimit}</span>
              )}
            </span>
          )}
        </div>
      </div>

      <div className="ai-prompt">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send();
          }}
          placeholder="Type what you need… (⌘↵ to send)"
          rows={2}
        />
        <button className="btn btn--primary" disabled={busy || prompt.trim().length < 3} onClick={() => send()}>
          <Icon name="spark" size={16} />
          {busy ? 'Pensando…' : 'Proponer'}
        </button>
      </div>

      {!proposal && !busy && (
        <div className="ai-suggestions">
          {SUGGESTIONS.map((s) => (
            <button key={s} className="ai-chip" onClick={() => { setPrompt(s); send(s); }}>
              {s}
            </button>
          ))}
        </div>
      )}

      {err && <div className="act-error" style={{ marginTop: 20 }}><Icon name="alert" size={16} />{err}</div>}

      {proposal && (
        <div className="proposal">
          <div className="proposal__summary">{proposal.summary}</div>

          {proposal.findings.length > 0 && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="card__head"><span className="card__title">Hallazgos</span></div>
              <div className="card__body" style={{ paddingTop: 4, paddingBottom: 4 }}>
                {proposal.findings.map((f, i) => (
                  <div key={i} className="finding">
                    <span className={`finding__bar finding__bar--${f.severity}`} />
                    <div>
                      <div className="finding__title">{f.title}</div>
                      <div className="finding__detail">{f.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {proposal.text && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="card__head"><span className="card__title">Texto redactado</span></div>
              <div className="card__body" style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {proposal.text}
              </div>
            </div>
          )}

          {protectedFieldObjs.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div className="eyebrow" style={{ marginBottom: 10 }}>Sensitive data · confirm one by one</div>
              {protectedFieldObjs.map((f) => {
                const on = confirmed.has(f.field);
                return (
                  <div key={f.field} className="fld-prot">
                    <div className="fld-prot__top">
                      <Icon name="lock" size={14} style={{ color: 'var(--amber)' }} />
                      <span className="fld-prot__tag">Requires confirmation</span>
                    </div>
                    <div className="fld-prot__label">{f.label}</div>
                    <div className="fld-prot__value">{renderValue(f.value)}</div>
                    <button className={`confirm-check${on ? ' confirm-check--on' : ''}`} onClick={() => toggle(f.field)}>
                      <span className="confirm-box"><Icon name="check" size={13} /></span>
                      He revisado este dato y es correcto
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {plainFields.length > 0 && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="card__head"><span className="card__title">Campos propuestos</span></div>
              <div className="card__body" style={{ paddingTop: 4, paddingBottom: 4 }}>
                {plainFields.map((f) => (
                  <div key={f.field} className="fld">
                    <div>
                      <div className="fld__label">{f.label}</div>
                      <div className="fld__value">{renderValue(f.value)}</div>
                    </div>
                    <span className="fld__conf">{Math.round(f.confidence * 100)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {proposal.questions.length > 0 && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="card__head"><span className="card__title">La IA necesita saber</span></div>
              <div className="card__body">
                <ul style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {proposal.questions.map((q, i) => (
                    <li key={i} style={{ fontSize: 13.5, display: 'flex', gap: 8 }}>
                      <span className="faint">·</span> {q}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {applied ? (
            <div className="ai-applied" style={{ marginTop: 20 }}>
              <Icon name="check" size={20} />
              Proposal applied. The confirmed data has been recorded under your authorship.
            </div>
          ) : (
            <div className="proposal__apply">
              <span className="proposal__note">
                {proposal.protectedFields.length > 0
                  ? `Faltan ${proposal.protectedFields.length - confirmed.size} de ${proposal.protectedFields.length} confirmaciones.`
                  : 'Esta propuesta no toca datos delicados.'}
              </span>
              <div className="row gap-8">
                <button className="btn btn--ghost" onClick={() => setProposal(null)}>Descartar</button>
                <button className="btn btn--accent" disabled={busy || !allConfirmed} onClick={apply}>
                  <Icon name="check" size={16} />
                  Aplicar propuesta
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </main>
  );
}
